// src/services/stockService.js
import {
    runTransaction,
    doc,
    collection,
    query,
    where,
    getDocs,
    writeBatch,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

const CURRENT_STOCK_COLLECTION = 'current-stock';
const STOCK_ENTRIES_COLLECTION = 'stock-entries';
const STOCK_OUTFLOWS_COLLECTION = 'stock-outflows';

// Cache for stock corrections to avoid repeated recalculations
let correctionCache = new Map();
let correctionCacheTimeout = null;
let isCorrectionRunning = false;

/**
 * Automatically correct stock inconsistency when detected
 */
export const autoCorrectStockInconsistency = async (rawMaterialId, lotNumber = null) => {
    // Prevent multiple simultaneous corrections
    if (isCorrectionRunning) {
        return { message: 'Correction already in progress', skipped: true };
    }

    const cacheKey = `${rawMaterialId}_${lotNumber || 'NOLOT'}`;

    // Check cache to avoid frequent corrections
    if (correctionCache.has(cacheKey)) {
        const cached = correctionCache.get(cacheKey);
        if (Date.now() - cached.timestamp < 5000) { // 5 second cache
            return { ...cached.result, cached: true };
        }
    }

    try {
        isCorrectionRunning = true;

        // Get current stock documents
        const stockQuery = query(
            collection(db, CURRENT_STOCK_COLLECTION),
            where('rawMaterialId', '==', rawMaterialId),
            ...(lotNumber ? [where('lotNumber', '==', lotNumber)] : [])
        );

        const stockSnapshot = await getDocs(stockQuery);

        // If no stock documents, nothing to correct
        if (stockSnapshot.empty) {
            return {
                rawMaterialId,
                lotNumber: lotNumber || null,
                message: 'No stock documents found',
                needsCorrection: false
            };
        }

        // Get entries and outflows
        const entriesQuery = query(
            collection(db, STOCK_ENTRIES_COLLECTION),
            where('rawMaterialId', '==', rawMaterialId),
            ...(lotNumber ? [where('lotNumber', '==', lotNumber)] : [])
        );

        const outflowsQuery = query(
            collection(db, STOCK_OUTFLOWS_COLLECTION),
            where('rawMaterialId', '==', rawMaterialId),
            ...(lotNumber ? [where('lotNumber', '==', lotNumber)] : [])
        );

        const [entriesSnapshot, outflowsSnapshot] = await Promise.all([
            getDocs(entriesQuery),
            getDocs(outflowsQuery)
        ]);

        // Calculate total from entries and outflows
        let calculatedTotal = 0;

        entriesSnapshot.forEach(entryDoc => {
            const data = entryDoc.data();
            let qty = 0;
            if (data.locationQuantities) {
                qty = Object.values(data.locationQuantities).reduce((sum, val) => sum + (Number(val) || 0), 0);
            } else if (data.quantity) {
                qty = Number(data.quantity) || 0;
            }
            // Skip transfers out in entries
            if (data.type !== 'TRANSFER_OUT') {
                calculatedTotal += qty;
            }
        });

        outflowsSnapshot.forEach(outflowDoc => {
            const data = outflowDoc.data();
            let qty = 0;
            if (data.locationQuantities) {
                qty = Object.values(data.locationQuantities).reduce((sum, val) => sum + (Number(val) || 0), 0);
            } else if (data.quantity) {
                qty = Number(data.quantity) || 0;
            }
            calculatedTotal -= qty;
        });

        // Calculate current total in stock
        let currentTotal = 0;
        const stockDocs = [];

        stockSnapshot.forEach(stockDoc => {
            const data = stockDoc.data();
            currentTotal += data.quantity || 0;
            stockDocs.push({
                id: stockDoc.id,
                locationId: data.locationId,
                quantity: data.quantity || 0,
                data: data
            });
        });

        const difference = calculatedTotal - currentTotal;
        const needsCorrection = Math.abs(difference) > 0.001;

        let corrected = false;
        let correctedCount = 0;

        if (needsCorrection && stockDocs.length > 0) {
            // Distribute the correction proportionally
            const batch = writeBatch(db);
            const correctionPerDoc = difference / stockDocs.length;

            for (const stockDoc of stockDocs) {
                const docRef = doc(db, CURRENT_STOCK_COLLECTION, stockDoc.id);
                const newQuantity = Math.max(0, (stockDoc.quantity || 0) + correctionPerDoc);

                if (Math.abs(stockDoc.quantity - newQuantity) > 0.001) {
                    batch.update(docRef, {
                        quantity: newQuantity,
                        lastUpdated: serverTimestamp(),
                        lastOperation: 'auto_correct',
                        correctedAt: serverTimestamp(),
                        previousQuantity: stockDoc.quantity,
                        correctionAmount: correctionPerDoc,
                        correctionReason: `Auto-correction: ${difference.toFixed(3)} units adjustment`
                    });
                    correctedCount++;
                }
            }

            if (correctedCount > 0) {
                await batch.commit();
                corrected = true;
            }
        }

        const result = {
            rawMaterialId,
            lotNumber: lotNumber || null,
            calculatedTotal,
            currentTotal,
            difference,
            needsCorrection,
            corrected,
            correctedCount,
            stockDocsCount: stockDocs.length,
            entriesCount: entriesSnapshot.size,
            outflowsCount: outflowsSnapshot.size,
            message: corrected
                ? `✅ Corrected ${correctedCount} stock documents (${difference.toFixed(3)} units)`
                : needsCorrection
                    ? `⚠️ Inconsistency detected but no documents to correct`
                    : `✅ Stock is consistent`
        };

        // Cache the result
        correctionCache.set(cacheKey, {
            timestamp: Date.now(),
            result: result
        });

        // Clear cache after 5 seconds
        if (correctionCacheTimeout) {
            clearTimeout(correctionCacheTimeout);
        }
        correctionCacheTimeout = setTimeout(() => {
            correctionCache.clear();
        }, 5000);

        return result;
    } catch (error) {
        console.error('Auto-correction failed:', error);
        throw error;
    } finally {
        isCorrectionRunning = false;
    }
};

/**
 * Auto-correct all stock on application startup
 */
export const autoCorrectAllStock = async () => {
    try {

        // Get all distinct material IDs from stock
        const stockSnapshot = await getDocs(collection(db, CURRENT_STOCK_COLLECTION));
        const materialMap = new Map();

        stockSnapshot.forEach(stockDoc => {
            const data = stockDoc.data();
            if (data.rawMaterialId) {
                const key = `${data.rawMaterialId}_${data.lotNumber || 'NOLOT'}`;
                if (!materialMap.has(key)) {
                    materialMap.set(key, {
                        rawMaterialId: data.rawMaterialId,
                        lotNumber: data.lotNumber || null
                    });
                }
            }
        });

        // Process each material
        const corrections = [];
        for (const [key, material] of materialMap) {
            try {
                const result = await autoCorrectStockInconsistency(material.rawMaterialId, material.lotNumber);
                if (result && result.corrected) {
                    corrections.push(result);
                }
            } catch (error) {
                console.error(`Failed to correct ${material.rawMaterialId}:`, error);
            }
        }

        const correctedCount = corrections.filter(c => c && c.corrected).length;

        return {
            totalMaterials: materialMap.size,
            corrections,
            correctedCount
        };
    } catch (error) {
        console.error('Auto-correction all stock failed:', error);
        throw error;
    }
};

/**
 * Update stock quantity with transaction - AUTOMATICALLY CORRECTS inconsistencies
 */
export const updateStockQuantityWithTransaction = async (stockId, quantityChange, operationType = 'add') => {
    if (!stockId) {
        throw new Error('Stock ID is required');
    }

    const stockRef = doc(db, CURRENT_STOCK_COLLECTION, stockId);

    try {
        const result = await runTransaction(db, async (transaction) => {
            const stockDoc = await transaction.get(stockRef);

            if (!stockDoc.exists()) {
                throw new Error(`Stock document ${stockId} does not exist`);
            }

            const currentData = stockDoc.data();
            const currentQuantity = currentData.quantity || 0;

            let newQuantity;
            if (operationType === 'add') {
                newQuantity = currentQuantity + quantityChange;
            } else if (operationType === 'subtract') {
                if (currentQuantity < quantityChange) {
                    throw new Error(`Insufficient stock: ${currentQuantity} < ${quantityChange}`);
                }
                newQuantity = currentQuantity - quantityChange;
            } else if (operationType === 'set') {
                newQuantity = quantityChange;
            } else {
                throw new Error(`Invalid operation type: ${operationType}`);
            }

            if (newQuantity < 0) {
                throw new Error(`Quantity would become negative: ${newQuantity}`);
            }

            transaction.update(stockRef, {
                quantity: newQuantity,
                lastUpdated: serverTimestamp(),
                lastOperation: operationType,
                lastQuantityChange: quantityChange,
                updatedAt: serverTimestamp()
            });

            return {
                stockId,
                oldQuantity: currentQuantity,
                newQuantity: newQuantity,
                change: quantityChange,
                rawMaterialId: currentData.rawMaterialId,
                lotNumber: currentData.lotNumber || null
            };
        });

        // After successful update, auto-correct if needed (async, don't await)
        if (result.rawMaterialId) {
            setTimeout(async () => {
                try {
                    await autoCorrectStockInconsistency(result.rawMaterialId, result.lotNumber);
                } catch (correctionError) {
                    console.warn('Auto-correction after update failed:', correctionError);
                }
            }, 100);
        }

        return result;
    } catch (error) {
        console.error('Stock update transaction failed:', error);
        throw error;
    }
};

/**
 * Update stock for multiple locations in a single transaction
 */
export const updateMultipleStockLocations = async (updates) => {
    if (!updates || updates.length === 0) {
        throw new Error('No updates provided');
    }

    try {
        const result = await runTransaction(db, async (transaction) => {
            const results = [];
            const stockRefs = [];
            const materialIds = new Set();

            for (const update of updates) {
                const { stockId } = update;
                if (!stockId) {
                    throw new Error('Stock ID is required for each update');
                }
                const stockRef = doc(db, CURRENT_STOCK_COLLECTION, stockId);
                stockRefs.push({ stockRef, update });
            }

            const docSnapshots = await Promise.all(
                stockRefs.map(({ stockRef }) => transaction.get(stockRef))
            );

            for (let i = 0; i < stockRefs.length; i++) {
                const { stockRef, update } = stockRefs[i];
                const stockDoc = docSnapshots[i];

                if (!stockDoc.exists()) {
                    throw new Error(`Stock document ${update.stockId} does not exist`);
                }

                const currentData = stockDoc.data();
                const currentQuantity = currentData.quantity || 0;
                const { quantityChange, operationType = 'add' } = update;

                let newQuantity;
                if (operationType === 'add') {
                    newQuantity = currentQuantity + quantityChange;
                } else if (operationType === 'subtract') {
                    if (currentQuantity < quantityChange) {
                        throw new Error(`Insufficient stock at ${update.stockId}: ${currentQuantity} < ${quantityChange}`);
                    }
                    newQuantity = currentQuantity - quantityChange;
                } else if (operationType === 'set') {
                    newQuantity = quantityChange;
                } else {
                    throw new Error(`Invalid operation type: ${operationType}`);
                }

                if (newQuantity < 0) {
                    throw new Error(`Quantity would become negative for ${update.stockId}`);
                }

                transaction.update(stockRef, {
                    quantity: newQuantity,
                    lastUpdated: serverTimestamp(),
                    lastOperation: operationType,
                    lastQuantityChange: quantityChange,
                    updatedAt: serverTimestamp()
                });

                results.push({
                    stockId: update.stockId,
                    oldQuantity: currentQuantity,
                    newQuantity: newQuantity,
                    change: quantityChange
                });

                if (currentData.rawMaterialId) {
                    materialIds.add({
                        rawMaterialId: currentData.rawMaterialId,
                        lotNumber: currentData.lotNumber || null
                    });
                }
            }

            return { results, materialIds: Array.from(materialIds) };
        });

        // Auto-correct all affected materials (async)
        for (const material of result.materialIds) {
            setTimeout(async () => {
                try {
                    await autoCorrectStockInconsistency(material.rawMaterialId, material.lotNumber);
                } catch (correctionError) {
                    console.warn('Auto-correction after multi-update failed:', correctionError);
                }
            }, 100);
        }

        return result.results;
    } catch (error) {
        console.error('Multiple stock update transaction failed:', error);
        throw error;
    }
};

/**
 * Create a new stock entry with automatic correction
 */
export const createOrUpdateStockEntry = async (stockData) => {
    const {
        rawMaterialId,
        locationId,
        quantity,
        lotNumber,
        expirationDate,
        rawMaterialCode,
        rawMaterialName,
        locationDescription,
        manufacturerDate,
        isProduct = false,
        ...rest
    } = stockData;

    if (!rawMaterialId || !locationId) {
        throw new Error('rawMaterialId and locationId are required');
    }

    if (!quantity || quantity <= 0) {
        throw new Error('Quantity must be greater than 0');
    }

    const stockQuery = query(
        collection(db, CURRENT_STOCK_COLLECTION),
        where('rawMaterialId', '==', rawMaterialId),
        where('locationId', '==', locationId),
        where('lotNumber', '==', lotNumber || null)
    );

    const snapshot = await getDocs(stockQuery);

    let stockRef;
    let isNew = false;

    if (!snapshot.empty) {
        stockRef = doc(db, CURRENT_STOCK_COLLECTION, snapshot.docs[0].id);
    } else {
        const newDocRef = doc(collection(db, CURRENT_STOCK_COLLECTION));
        stockRef = newDocRef;
        isNew = true;
    }

    try {
        const result = await runTransaction(db, async (transaction) => {
            let currentData = {};
            let currentQuantity = 0;

            if (!isNew) {
                const stockDoc = await transaction.get(stockRef);
                if (stockDoc.exists()) {
                    currentData = stockDoc.data();
                    currentQuantity = currentData.quantity || 0;
                }
            }

            const newQuantity = currentQuantity + quantity;

            if (isNew) {
                transaction.set(stockRef, {
                    rawMaterialId,
                    rawMaterialCode: rawMaterialCode || '',
                    rawMaterialName: rawMaterialName || '',
                    locationId,
                    locationDescription: locationDescription || '',
                    quantity: newQuantity,
                    lotNumber: lotNumber || null,
                    expirationDate: expirationDate || null,
                    manufacturerDate: manufacturerDate || null,
                    isProduct: isProduct || false,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    lastUpdated: serverTimestamp(),
                    lastOperation: 'create',
                    lastQuantityChange: quantity,
                    ...rest
                });
            } else {
                const updateData = {
                    quantity: newQuantity,
                    updatedAt: serverTimestamp(),
                    lastUpdated: serverTimestamp(),
                    lastOperation: 'add',
                    lastQuantityChange: quantity,
                };

                if (rawMaterialCode) updateData.rawMaterialCode = rawMaterialCode;
                if (rawMaterialName) updateData.rawMaterialName = rawMaterialName;
                if (locationDescription) updateData.locationDescription = locationDescription;
                if (expirationDate) updateData.expirationDate = expirationDate;
                if (manufacturerDate) updateData.manufacturerDate = manufacturerDate;
                if (isProduct !== undefined) updateData.isProduct = isProduct;

                transaction.update(stockRef, updateData);
            }

            return {
                isNew,
                stockId: stockRef.id,
                oldQuantity: currentQuantity,
                newQuantity: newQuantity,
                change: quantity,
                rawMaterialId,
                lotNumber: lotNumber || null
            };
        });

        // Auto-correct after creating/updating (async)
        setTimeout(async () => {
            try {
                await autoCorrectStockInconsistency(rawMaterialId, lotNumber);
            } catch (correctionError) {
                console.warn('Auto-correction after entry failed:', correctionError);
            }
        }, 200);

        return result;
    } catch (error) {
        console.error('Create/update stock entry transaction failed:', error);
        throw error;
    }
};

/**
 * Perform a stock transfer between locations
 */
export const transferStockBetweenLocations = async (sourceStockId, targetStockId, quantity, metadata = {}) => {
    if (!sourceStockId || !targetStockId || !quantity || quantity <= 0) {
        throw new Error('Valid sourceStockId, targetStockId, and quantity are required');
    }

    try {
        const result = await runTransaction(db, async (transaction) => {
            const sourceRef = doc(db, CURRENT_STOCK_COLLECTION, sourceStockId);
            const targetRef = doc(db, CURRENT_STOCK_COLLECTION, targetStockId);

            const [sourceDoc, targetDoc] = await Promise.all([
                transaction.get(sourceRef),
                transaction.get(targetRef)
            ]);

            if (!sourceDoc.exists()) {
                throw new Error(`Source stock document ${sourceStockId} does not exist`);
            }

            const sourceData = sourceDoc.data();
            const sourceQuantity = sourceData.quantity || 0;

            if (sourceQuantity < quantity) {
                throw new Error(`Insufficient stock at source: ${sourceQuantity} < ${quantity}`);
            }

            transaction.update(sourceRef, {
                quantity: sourceQuantity - quantity,
                lastUpdated: serverTimestamp(),
                lastOperation: 'transfer_out',
                lastQuantityChange: -quantity,
                updatedAt: serverTimestamp()
            });

            let targetQuantity = 0;
            let isTargetNew = false;

            if (targetDoc.exists()) {
                targetQuantity = targetDoc.data().quantity || 0;
                transaction.update(targetRef, {
                    quantity: targetQuantity + quantity,
                    lastUpdated: serverTimestamp(),
                    lastOperation: 'transfer_in',
                    lastQuantityChange: quantity,
                    updatedAt: serverTimestamp()
                });
            } else {
                isTargetNew = true;
                transaction.set(targetRef, {
                    rawMaterialId: sourceData.rawMaterialId,
                    rawMaterialCode: sourceData.rawMaterialCode,
                    rawMaterialName: sourceData.rawMaterialName,
                    locationId: metadata.targetLocationId || 'UNKNOWN',
                    locationDescription: metadata.targetLocationDescription || 'Unknown Location',
                    quantity: quantity,
                    lotNumber: sourceData.lotNumber || null,
                    expirationDate: sourceData.expirationDate || null,
                    manufacturerDate: sourceData.manufacturerDate || null,
                    isProduct: sourceData.isProduct || false,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    lastUpdated: serverTimestamp(),
                    lastOperation: 'transfer_in',
                    lastQuantityChange: quantity
                });
            }

            return {
                sourceStockId,
                targetStockId,
                isTargetNew,
                sourceOldQuantity: sourceQuantity,
                sourceNewQuantity: sourceQuantity - quantity,
                targetOldQuantity: targetQuantity,
                targetNewQuantity: targetQuantity + quantity,
                transferredQuantity: quantity,
                rawMaterialId: sourceData.rawMaterialId,
                lotNumber: sourceData.lotNumber || null
            };
        });

        // Auto-correct after transfer (async)
        if (result.rawMaterialId) {
            setTimeout(async () => {
                try {
                    await autoCorrectStockInconsistency(result.rawMaterialId, result.lotNumber);
                } catch (correctionError) {
                    console.warn('Auto-correction after transfer failed:', correctionError);
                }
            }, 200);
        }

        return result;
    } catch (error) {
        console.error('Stock transfer transaction failed:', error);
        throw error;
    }
};

/**
 * Delete a stock document (with transaction)
 */
export const deleteStockDocument = async (stockId) => {
    if (!stockId) {
        throw new Error('Stock ID is required');
    }

    let rawMaterialId;
    let lotNumber;

    try {
        await runTransaction(db, async (transaction) => {
            const stockRef = doc(db, CURRENT_STOCK_COLLECTION, stockId);
            const stockDoc = await transaction.get(stockRef);

            if (!stockDoc.exists()) {
                throw new Error(`Stock document ${stockId} does not exist`);
            }

            const data = stockDoc.data();
            rawMaterialId = data.rawMaterialId;
            lotNumber = data.lotNumber || null;

            transaction.delete(stockRef);
        });

        // Auto-correct after deletion (async)
        if (rawMaterialId) {
            setTimeout(async () => {
                try {
                    await autoCorrectStockInconsistency(rawMaterialId, lotNumber);
                } catch (correctionError) {
                    console.warn('Auto-correction after deletion failed:', correctionError);
                }
            }, 200);
        }

        return { success: true, stockId };
    } catch (error) {
        console.error('Delete stock document transaction failed:', error);
        throw error;
    }
};

/**
 * Bulk create stock entries (for initial inventory or batch imports)
 */
export const bulkCreateStockEntries = async (entries) => {
    if (!entries || entries.length === 0) {
        throw new Error('No entries provided');
    }

    try {
        const results = await runTransaction(db, async (transaction) => {
            const processedResults = [];

            for (const entry of entries) {
                const {
                    rawMaterialId,
                    locationId,
                    quantity,
                    lotNumber,
                    expirationDate,
                    rawMaterialCode,
                    rawMaterialName,
                    locationDescription,
                    manufacturerDate,
                    isProduct = false
                } = entry;

                if (!rawMaterialId || !locationId) {
                    throw new Error('rawMaterialId and locationId are required for each entry');
                }

                const stockQuery = query(
                    collection(db, CURRENT_STOCK_COLLECTION),
                    where('rawMaterialId', '==', rawMaterialId),
                    where('locationId', '==', locationId),
                    where('lotNumber', '==', lotNumber || null)
                );

                const snapshot = await getDocs(stockQuery);
                let stockRef;
                let isNew = false;

                if (!snapshot.empty) {
                    stockRef = doc(db, CURRENT_STOCK_COLLECTION, snapshot.docs[0].id);
                    const stockDoc = await transaction.get(stockRef);
                    const currentQuantity = stockDoc.exists() ? (stockDoc.data().quantity || 0) : 0;

                    transaction.update(stockRef, {
                        quantity: currentQuantity + quantity,
                        updatedAt: serverTimestamp(),
                        lastUpdated: serverTimestamp(),
                        lastOperation: 'bulk_add',
                        lastQuantityChange: quantity
                    });

                    processedResults.push({
                        stockId: stockRef.id,
                        isNew: false,
                        oldQuantity: currentQuantity,
                        newQuantity: currentQuantity + quantity,
                        change: quantity
                    });
                } else {
                    stockRef = doc(collection(db, CURRENT_STOCK_COLLECTION));
                    transaction.set(stockRef, {
                        rawMaterialId,
                        rawMaterialCode: rawMaterialCode || '',
                        rawMaterialName: rawMaterialName || '',
                        locationId,
                        locationDescription: locationDescription || '',
                        quantity: quantity,
                        lotNumber: lotNumber || null,
                        expirationDate: expirationDate || null,
                        manufacturerDate: manufacturerDate || null,
                        isProduct: isProduct || false,
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp(),
                        lastUpdated: serverTimestamp(),
                        lastOperation: 'bulk_create',
                        lastQuantityChange: quantity
                    });

                    processedResults.push({
                        stockId: stockRef.id,
                        isNew: true,
                        oldQuantity: 0,
                        newQuantity: quantity,
                        change: quantity
                    });
                }
            }

            return processedResults;
        });

        // Auto-correct all affected materials
        const materialIds = new Set();
        entries.forEach(entry => {
            materialIds.add({
                rawMaterialId: entry.rawMaterialId,
                lotNumber: entry.lotNumber || null
            });
        });

        for (const material of materialIds) {
            setTimeout(async () => {
                try {
                    await autoCorrectStockInconsistency(material.rawMaterialId, material.lotNumber);
                } catch (correctionError) {
                    console.warn('Auto-correction after bulk entry failed:', correctionError);
                }
            }, 300);
        }

        return results;
    } catch (error) {
        console.error('Bulk stock entry transaction failed:', error);
        throw error;
    }
};

/**
 * Get stock summary for a specific material
 */
export const getStockSummary = async (rawMaterialId) => {
    try {
        const stockQuery = query(
            collection(db, CURRENT_STOCK_COLLECTION),
            where('rawMaterialId', '==', rawMaterialId)
        );

        const stockSnapshot = await getDocs(stockQuery);
        const locations = [];
        let totalQuantity = 0;

        stockSnapshot.forEach(stockDoc => {
            const data = stockDoc.data();
            totalQuantity += data.quantity || 0;
            locations.push({
                stockId: stockDoc.id,
                locationId: data.locationId,
                locationDescription: data.locationDescription,
                quantity: data.quantity,
                lotNumber: data.lotNumber,
                expirationDate: data.expirationDate
            });
        });

        return {
            rawMaterialId,
            totalQuantity,
            locationCount: locations.length,
            locations: locations.sort((a, b) => a.locationId.localeCompare(b.locationId))
        };
    } catch (error) {
        console.error('Get stock summary failed:', error);
        throw error;
    }
};

/**
 * Get all stock for a specific location
 */
export const getStockByLocation = async (locationId) => {
    try {
        const stockQuery = query(
            collection(db, CURRENT_STOCK_COLLECTION),
            where('locationId', '==', locationId)
        );

        const stockSnapshot = await getDocs(stockQuery);
        const items = [];

        stockSnapshot.forEach(stockDoc => {
            const data = stockDoc.data();
            items.push({
                stockId: stockDoc.id,
                ...data
            });
        });

        return items.sort((a, b) => (a.rawMaterialCode || '').localeCompare(b.rawMaterialCode || ''));
    } catch (error) {
        console.error('Get stock by location failed:', error);
        throw error;
    }
};

/**
 * Get all stock for a specific lot
 */
export const getStockByLot = async (lotNumber) => {
    if (!lotNumber) {
        throw new Error('Lot number is required');
    }

    try {
        const stockQuery = query(
            collection(db, CURRENT_STOCK_COLLECTION),
            where('lotNumber', '==', lotNumber)
        );

        const stockSnapshot = await getDocs(stockQuery);
        const items = [];

        stockSnapshot.forEach(stockDoc => {
            const data = stockDoc.data();
            items.push({
                stockId: stockDoc.id,
                ...data
            });
        });

        return items.sort((a, b) => (a.rawMaterialCode || '').localeCompare(b.rawMaterialCode || ''));
    } catch (error) {
        console.error('Get stock by lot failed:', error);
        throw error;
    }
};

/**
 * Verify stock consistency - checks for negative quantities and zero balances
 */
export const verifyAndFixStockConsistency = async () => {
    try {
        const stockSnapshot = await getDocs(collection(db, CURRENT_STOCK_COLLECTION));

        const groups = new Map();
        const inconsistencies = [];

        stockSnapshot.forEach(stockDoc => {
            const data = stockDoc.data();
            const key = `${data.rawMaterialId}_${data.lotNumber || ''}_${data.locationId}`;

            if (!groups.has(key)) {
                groups.set(key, {
                    rawMaterialId: data.rawMaterialId,
                    lotNumber: data.lotNumber,
                    locationId: data.locationId,
                    totalQuantity: 0,
                    documents: []
                });
            }

            const group = groups.get(key);
            group.totalQuantity += data.quantity || 0;
            group.documents.push({
                id: stockDoc.id,
                quantity: data.quantity,
                locationId: data.locationId
            });
        });

        const negativeItems = [];
        const zeroItems = [];

        groups.forEach((group, key) => {
            if (group.totalQuantity < 0) {
                negativeItems.push({ key, group });
                inconsistencies.push({
                    type: 'negative',
                    key,
                    group
                });
            }
            if (group.totalQuantity === 0) {
                zeroItems.push({ key, group });
                inconsistencies.push({
                    type: 'zero',
                    key,
                    group
                });
            }
        });

        // Auto-fix negative quantities by setting them to 0
        if (negativeItems.length > 0) {
            const batch = writeBatch(db);
            let fixedCount = 0;

            for (const item of negativeItems) {
                for (const stockDoc of item.group.documents) {
                    const docRef = doc(db, CURRENT_STOCK_COLLECTION, stockDoc.id);
                    batch.update(docRef, {
                        quantity: 0,
                        lastUpdated: serverTimestamp(),
                        lastOperation: 'fix_negative',
                        fixedAt: serverTimestamp(),
                        fixedBy: 'system'
                    });
                    fixedCount++;
                }
            }

            if (fixedCount > 0) {
                await batch.commit();
            }
        }

        return {
            totalGroups: groups.size,
            totalDocuments: stockSnapshot.size,
            negativeItems,
            zeroItems,
            inconsistencies,
            fixedCount: negativeItems.length,
            groups: Array.from(groups.values()),
            message: negativeItems.length > 0
                ? `${negativeItems.length} itens com quantidade negativa foram corrigidos para 0.`
                : 'Nenhuma inconsistência grave encontrada.'
        };
    } catch (error) {
        console.error('Verification failed:', error);
        throw error;
    }
};

/**
 * Recalculate stock from entries and outflows
 */
export const recalculateStockFromEntries = async (rawMaterialId, lotNumber = null) => {
    try {
        // Get all entries for this material
        const entriesQuery = query(
            collection(db, STOCK_ENTRIES_COLLECTION),
            where('rawMaterialId', '==', rawMaterialId),
            ...(lotNumber ? [where('lotNumber', '==', lotNumber)] : [])
        );

        const outflowsQuery = query(
            collection(db, STOCK_OUTFLOWS_COLLECTION),
            where('rawMaterialId', '==', rawMaterialId),
            ...(lotNumber ? [where('lotNumber', '==', lotNumber)] : [])
        );

        const [entriesSnapshot, outflowsSnapshot] = await Promise.all([
            getDocs(entriesQuery),
            getDocs(outflowsQuery)
        ]);

        // Calculate net quantity
        let totalEntries = 0;
        let totalOutflows = 0;

        entriesSnapshot.forEach(entryDoc => {
            const data = entryDoc.data();
            let qty = 0;
            if (data.locationQuantities) {
                qty = Object.values(data.locationQuantities).reduce((sum, val) => sum + (Number(val) || 0), 0);
            } else if (data.quantity) {
                qty = Number(data.quantity) || 0;
            }
            // Skip transfers out in entries
            if (data.type !== 'TRANSFER_OUT') {
                totalEntries += qty;
            }
        });

        outflowsSnapshot.forEach(outflowDoc => {
            const data = outflowDoc.data();
            let qty = 0;
            if (data.locationQuantities) {
                qty = Object.values(data.locationQuantities).reduce((sum, val) => sum + (Number(val) || 0), 0);
            } else if (data.quantity) {
                qty = Number(data.quantity) || 0;
            }
            totalOutflows += qty;
        });

        const calculatedTotal = totalEntries - totalOutflows;

        // Get current stock documents
        const stockQuery = query(
            collection(db, CURRENT_STOCK_COLLECTION),
            where('rawMaterialId', '==', rawMaterialId),
            ...(lotNumber ? [where('lotNumber', '==', lotNumber)] : [])
        );

        const stockSnapshot = await getDocs(stockQuery);
        let currentStockTotal = 0;
        const stockDocs = [];

        stockSnapshot.forEach(stockDoc => {
            const data = stockDoc.data();
            currentStockTotal += data.quantity || 0;
            stockDocs.push({
                id: stockDoc.id,
                locationId: data.locationId,
                quantity: data.quantity
            });
        });

        const difference = calculatedTotal - currentStockTotal;
        const needsCorrection = Math.abs(difference) > 0.001;

        // If correction is needed, update the stock documents
        if (needsCorrection && stockDocs.length > 0) {
            const batch = writeBatch(db);
            const correctionPerDoc = difference / stockDocs.length;

            for (const stockDoc of stockDocs) {
                const docRef = doc(db, CURRENT_STOCK_COLLECTION, stockDoc.id);
                const newQuantity = Math.max(0, (stockDoc.quantity || 0) + correctionPerDoc);
                batch.update(docRef, {
                    quantity: newQuantity,
                    lastUpdated: serverTimestamp(),
                    lastOperation: 'recalculate',
                    recalculatedAt: serverTimestamp(),
                    recalculatedBy: 'system',
                    previousQuantity: stockDoc.quantity
                });
            }

            await batch.commit();
        }

        return {
            rawMaterialId,
            lotNumber: lotNumber || null,
            totalEntries,
            totalOutflows,
            calculatedTotal,
            currentStockTotal,
            difference,
            needsCorrection,
            entriesCount: entriesSnapshot.size,
            outflowsCount: outflowsSnapshot.size,
            stockDocsCount: stockDocs.length,
            corrected: needsCorrection,
            message: needsCorrection
                ? `Correção aplicada: ${difference.toFixed(3)} unidades ajustadas.`
                : 'Estoque consistente, nenhuma correção necessária.'
        };
    } catch (error) {
        console.error('Recalculation failed:', error);
        throw error;
    }
};